//------------------------------------------------------------------------------
// メニュー
//------------------------------------------------------------------------------
import React from 'react';
import MenuItem from './menu-item';

/**
 * メニュー項目グループ
 */
export default class MenuGroup extends MenuItem {

  /** 親項目 */
  private _parent: MenuGroup | null;

  /** 項目一覧 */
  private _children: MenuItem[];

  /**
   * コンストラクタ
   *
   * @param id 項目ID
   * @param key 項目キー
   * @param label 項目名
   */
  constructor(
    id: string,
    key: string = id,
    label: string = '')
  {
    super(id, key, label);
    this._parent = null;
    this._children = [];
  }

  /**
   * 子項目一覧
   */
  children(): MenuItem[] {
    return this._children;
  }

  /**
   * 子項目追加
   *
   * @param id 子項目ID
   * @param label 子項目ラベル
   * @param link 遷移先画面パス
   * @param icon 項目アイコン
   * @return このオブジェクト
   */
  add(id: string, label: string, link: string, icon?: React.ReactNode): this {

    // 子項目作成
    const key = `${this.key}/${id}`;
    const item = new MenuItem(id, key, label, link, icon);

    // 一覧に追加
    this._children.push(item);
    return this;
  }

  /**
   * 子項目追加終了
   *
   * @return 親項目
   */
  end(): MenuGroup {
    if (this._parent === null) {
      throw new Error(
          'Not defined parent of menu item'
          + ` key=${this.key}, label=${this.label}`);
    }
    return this._parent;
  }

  /**
   * 子項目グループ追加
   *
   * @param id 子項目ID
   * @param label 子項目ラベル
   */
  group(id: string, label: string): MenuGroup {

    // 項目グループ作成
    const key = `${this.key}/${id}`;
    const item = new MenuGroup(id, key, label);
    item._parent = this;
    item._children = [];
    
    // 一覧に追加
    this._children.push(item);
    return item;
  }

  /**
   * 子項目の抽出 (フィルタリング)
   *
   * @param available 有効判定関数
   * @return 有効判定を適用した新しい項目グループ
   */
  filter(available: (item: MenuItem) => boolean): MenuGroup {

    // 新しい項目グループを作成
    const newItem = new MenuGroup(this.id, this.key, this.label);
    newItem._parent = this._parent;

    // 子項目をフィルタリング
    const newChildren = [];
    for (const child of this._children) {

      // 子項目が項目グループなら再帰的にフィルタリング
      if (child instanceof MenuGroup) {
        const newChild = child.filter(available);

        // 孫項目が存在すれば有効にする
        if (newChild.children().length) {
          newChildren.push(newChild);
        }
        continue;
      }

      // 子項目の有効判定
      if (available(child)) {
        newChildren.push(child);
      }
    }
    newItem._children = newChildren;

    // 新しい項目グループを返す
    return newItem;
  }

  /**
   * 子項目の検索
   */
  find(condition: (item: MenuItem) => boolean): MenuItem | null {

    // 子項目の検索
    for (const child of this._children) {

      // 子項目が項目グループなら再帰的に検索
      if (child instanceof MenuGroup) {
        const item = child.find(condition);
        if (item) {
          return item;
        }
      }

      // 子項目の条件判定
      if (condition(child)) {
        return child;
      }
    }

    // 子項目なし
    return null;
  }
}
